/* eslint-disable no-magic-numbers */
import ClearIcon from '@mui/icons-material/Clear';
import { Autocomplete, Chip, CircularProgress, Grid, IconButton, TextField } from '@mui/material';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const maxNumbersOfAssignees = 5;

const MultipleInterviewersSelector = ({
	labels,
	classes,
	loadingPeople,
	people,
	getOptionLabelFromOption,
	setAssignee,
	assignee,
	handleSearchChange,
	selectedForm,
	setSelectedForm,
	forms,
	setNumOfForms,
}) => {
	const {
		noOptionsText,
		loadingText,
		assignInterviewerLabel,
		typeToSearchTextForMultiple,
		searchingPeople,
		optionsText,
		autocompleteArrowNavigation,
		formSelectionNavigation,
		selectFormLabel,
		clearSelectionLabel,
		formsOptionsText,
	} = labels;

	const handleAssigneeChange = useCallback(
		(event, value) => {
			const uniqueValues = value
				.filter((v, index, self) => index === self.findIndex((i) => i.id === v.id))
				.slice(0, maxNumbersOfAssignees);
			setAssignee(uniqueValues);
		},
		[setAssignee],
	);

	const handleFormChange = useCallback(
		(event, value) => {
			setSelectedForm(value);
		},
		[setSelectedForm],
	);

	const handleClearFormSelection = () => handleFormChange(null, null);

	const handleInputChange = useCallback(
		(event) => {
			const { value } = event.target;
			const numberOfFormsMatched = forms.filter((form) =>
				Object.values(form.displayNamesMap).some((name) =>
					name.toLowerCase().includes(value.toLowerCase()),
				),
			).length;
			setNumOfForms(numberOfFormsMatched);
		},
		[forms, setNumOfForms],
	);

	const peopleLiveRegionRef = useRef(null);
	const peopleAutocompleteRef = useRef(null);
	const { t } = useTranslation(['candidate-feedback']);

	useEffect(() => {
		if (peopleLiveRegionRef.current) {
			let label = `${assignInterviewerLabel}, `;

			if (loadingPeople) {
				label += searchingPeople;
			} else if (people.length === 0) {
				label += noOptionsText;
			} else {
				label += `${optionsText} ${autocompleteArrowNavigation}`;
			}

			peopleLiveRegionRef.current.textContent = '';
			setTimeout(() => {
				if (peopleLiveRegionRef.current) {
					peopleLiveRegionRef.current.textContent = label;
				}
			}, 100);
		}
	}, [
		people,
		loadingPeople,
		noOptionsText,
		searchingPeople,
		optionsText,
		assignInterviewerLabel,
		autocompleteArrowNavigation,
	]);

	const handleChipDelete = (optionToRemove, currentValue, handleChangeFn, ref) => {
		const newValue = currentValue.filter((v) => v.id !== optionToRemove.id);
		handleChangeFn(null, newValue);
		if (peopleLiveRegionRef.current) {
			// eslint-disable-next-line prefer-destructuring
			const name = getOptionLabelFromOption(optionToRemove).split(',')[0];
			peopleLiveRegionRef.current.textContent = '';
			setTimeout(() => {
				peopleLiveRegionRef.current.textContent = t('removed_assigned_interviewer', { name });
			}, 100);
		}
		setTimeout(() => {
			const input = ref.current?.querySelector('input');

			if (input) {
				input.blur();
				input.focus();
			}
		}, 150);
	};

	return (
		<Grid container spacing={1}>
			<Grid item xs={12} md={12}>
				<Autocomplete
					multiple
					ref={peopleAutocompleteRef}
					id="feedback-assignee-combo-box"
					data-testid="feedback-assignee-autocomplete"
					sx={classes.gridItemContent}
					size="small"
					options={people}
					getOptionLabel={getOptionLabelFromOption}
					onChange={handleAssigneeChange}
					loading={loadingPeople}
					noOptionsText={noOptionsText}
					loadingText={loadingText}
					disablePortal={false}
					value={assignee || null}
					clearOnEscape
					disableClearable
					renderTags={(value, getTagProps) =>
						value.map((option, index) => (
							<Chip
								key={option.id}
								label={getOptionLabelFromOption(option)}
								{...getTagProps({ index })}
								onDelete={() =>
									handleChipDelete(option, value, handleAssigneeChange, peopleAutocompleteRef)
								}
								deleteIcon={
									<IconButton
										aria-label={t('remove_assigned_interviewer', {
											name: getOptionLabelFromOption(option).split(',')[0],
										})}
										tabIndex={0}
										data-testid="remove-assignee-button"
									>
										<ClearIcon />
									</IconButton>
								}
							/>
						))
					}
					renderInput={(params) => (
						<TextField
							data-testid="autocomplete-textfield"
							{...params}
							label={assignInterviewerLabel}
							onChange={handleSearchChange}
							variant="outlined"
							helperText={typeToSearchTextForMultiple}
							InputProps={{
								...params.InputProps,
								onFocus: (e) => {
									e.target.setAttribute(
										'aria-label',
										`${assignInterviewerLabel}, ${loadingPeople ? searchingPeople : optionsText}`,
									);
								},
								endAdornment: (
									<>
										{loadingPeople ? <CircularProgress color="inherit" size={20} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>
				<span
					id="multiselect-autocomplete-live-region"
					aria-live="assertive"
					role="status"
					className="screenreader"
					ref={peopleLiveRegionRef}
				/>
			</Grid>
			<Grid item xs={12} md={12}>
				<Autocomplete
					id="feedback-form-autocomplete"
					data-testid="feedback-form-autocomplete"
					sx={classes.gridItemContent}
					size="small"
					options={forms}
					getOptionLabel={(option) => Object.values(option.displayNamesMap)[0]}
					onChange={handleFormChange}
					value={selectedForm || null}
					disablePortal={false}
					clearOnEscape
					disableClearable
					renderInput={(params) => (
						<TextField
							{...params}
							label={selectFormLabel}
							variant="outlined"
							onChange={handleInputChange}
							InputProps={{
								...params.InputProps,
								onFocus: (e) => {
									e.target.setAttribute(
										'aria-label',
										`${selectFormLabel}, ${formsOptionsText} ${formSelectionNavigation}`,
									);
								},
								endAdornment: (
									<>
										{params.inputProps.value && (
											<IconButton
												onClick={handleClearFormSelection}
												data-testid="clear-selection-button"
												aria-label={clearSelectionLabel}
												sx={{
													padding: 0,
													marginLeft: 8,
												}}
											>
												<ClearIcon />
											</IconButton>
										)}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>
				<span
					id="autocomplete-form-live-region"
					role="status"
					aria-label={`${selectFormLabel}, ${formsOptionsText} ${formSelectionNavigation}`}
					className="screenreader"
				/>
			</Grid>
		</Grid>
	);
};

export default MultipleInterviewersSelector;
